import { useEffect, useState, type FormEvent } from 'react';
import { useBudgets } from '@/hooks/useBudgets';
import { useCategories } from '@/hooks/useCategories';
import { formatCurrency, formatMonth, getPercentage, cn } from '@/lib/utils';
import type { CreateBudgetData } from '@/lib/types';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Target,
} from 'lucide-react';

export default function BudgetsPage() {
  const { budgets, loading, error, fetchBudgets, createBudget, deleteBudget } =
    useBudgets();
  const { categories, fetchCategories } = useCategories();

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formCategory, setFormCategory] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchBudgets(selectedMonth, selectedYear);
  }, [fetchBudgets, selectedMonth, selectedYear]);

  function navigateMonth(direction: -1 | 1) {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const data: CreateBudgetData = {
        categoryId: formCategory,
        amount: parseFloat(formAmount),
        month: selectedMonth,
        year: selectedYear,
      };
      await createBudget(data);
      setShowForm(false);
      setFormCategory('');
      setFormAmount('');
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to create budget'
      );
    } finally {
      setFormLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this budget?')) return;
    await deleteBudget(id);
  }

  const totalBudgeted = budgets.reduce((sum, b) => sum + b.amount, 0);
  const totalSpent = budgets.reduce((sum, b) => sum + (b.spent ?? 0), 0);

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Budgets</h1>
          <p className="mt-1 text-gray-400">
            Track spending against your budget limits
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus size={18} className="mr-2" />
          Add Budget
        </button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="btn-secondary p-2"
        >
          <ChevronLeft size={18} />
        </button>
        <h2 className="min-w-[200px] text-center text-lg font-semibold text-white">
          {formatMonth(selectedMonth, selectedYear)}
        </h2>
        <button
          onClick={() => navigateMonth(1)}
          className="btn-secondary p-2"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card">
          <p className="text-sm text-gray-400">Total Budgeted</p>
          <p className="mt-1 text-xl font-bold text-white">
            {formatCurrency(totalBudgeted)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">Total Spent</p>
          <p className="mt-1 text-xl font-bold text-accent-red">
            {formatCurrency(totalSpent)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">Remaining</p>
          <p
            className={cn(
              'mt-1 text-xl font-bold',
              totalBudgeted - totalSpent >= 0
                ? 'text-accent-green'
                : 'text-accent-red'
            )}
          >
            {formatCurrency(totalBudgeted - totalSpent)}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
          {error}
        </div>
      )}

      {/* Budgets List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      ) : budgets.length === 0 ? (
        <div className="card py-12 text-center">
          <Target className="mx-auto h-12 w-12 text-gray-600" />
          <p className="mt-4 text-gray-500">No budgets set for this month</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary mt-4"
          >
            <Plus size={18} className="mr-2" />
            Create your first budget
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {budgets.map((budget) => {
            const spent = budget.spent ?? 0;
            const percentage = getPercentage(spent, budget.amount);
            const isOverBudget = spent > budget.amount;
            const remaining = budget.amount - spent;

            return (
              <div
                key={budget.id}
                className="card flex items-start justify-between gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    {budget.categoryColor && (
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: budget.categoryColor }}
                      />
                    )}
                    <h3 className="font-medium text-white">
                      {budget.categoryName || 'Unknown Category'}
                    </h3>
                  </div>

                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="text-gray-400">
                        {formatCurrency(spent)} of {formatCurrency(budget.amount)}
                      </span>
                      <span
                        className={cn(
                          'font-medium',
                          isOverBudget ? 'text-accent-red' : 'text-gray-400'
                        )}
                      >
                        {percentage}%
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-surface-tertiary">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-500',
                          isOverBudget
                            ? 'bg-accent-red'
                            : percentage >= 80
                              ? 'bg-accent-yellow'
                              : 'bg-brand-500'
                        )}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                    <p
                      className={cn(
                        'mt-1 text-xs',
                        isOverBudget ? 'text-accent-red' : 'text-gray-500'
                      )}
                    >
                      {isOverBudget
                        ? `Over budget by ${formatCurrency(Math.abs(remaining))}`
                        : `${formatCurrency(remaining)} remaining`}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(budget.id)}
                  className="mt-1 rounded-lg p-2 text-gray-500 transition-colors hover:bg-accent-red/10 hover:text-accent-red"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Budget Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-md animate-slideUp">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Add Budget</h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormError('');
                }}
                className="rounded-lg p-1 text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
                  {formError}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Category
                </label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  required
                  className="input-base"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Budget Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  className="input-base"
                />
              </div>

              <p className="text-sm text-gray-500">
                For {formatMonth(selectedMonth, selectedYear)}
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormError('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="btn-primary flex-1"
                >
                  {formLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Add Budget'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
